// agent-parts.js — typed parts model for one assistant turn.
// The pure reducer (applyPartEvent) is the single source of truth; renderers
// paint these parts. Reducer is unit-tested (agent-parts.test.mjs);
// renderers are covered by Puppeteer E2E.
//
// Import guard: this file is split into two sections.
//   1. Pure data layer (no DOM) — reducer + factories (safe in Node / unit tests)
//   2. Rendering layer     — DOM builders, registered via PART_RENDERERS
//
// Section 2 imports from agent-parts-md.js and agent-activity.js.
// Those imports are only evaluated when the browser loads the module; they do
// NOT call any DOM API at import time. All DOM work lives inside the exported
// render functions, so `node -e "import('./agent-parts.js')"` is safe.

let _pid = 0;
const nextId = (prefix) => `${prefix}-${++_pid}`;

// --- part factories -------------------------------------------------------
export function textPart(md = "") { return { id: nextId("tx"), type: "text", md, streaming: true }; }
export function reasoningPart(md = "") {
  return { id: nextId("rs"), type: "reasoning", md, streaming: true, title: "", durationMs: null, startedAt: Date.now() };
}
export function toolPart(ev) {
  return {
    id: ev.id || nextId("tl"), type: "tool",
    tool: ev.tool || "tool", subtype: ev.subtype || "generic", args: ev.args || {},
    verb: ev.verb || "", target: ev.target || "", why: ev.why || "", risk: ev.risk || "read",
    status: "running", startedAt: Date.now(), durationMs: null, result: null,
  };
}

const replaceLast = (arr, v) => { const c = arr.slice(); c[c.length - 1] = v; return c; };
const replaceAt = (arr, i, v) => { const c = arr.slice(); c[i] = v; return c; };

// --- the pure reducer -----------------------------------------------------
export function applyPartEvent(parts, ev) {
  switch (ev.type) {
    case "message_delta": {
      const last = parts[parts.length - 1];
      if (last && last.type === "text" && last.streaming)
        return replaceLast(parts, { ...last, md: (last.md || "") + (ev.text || "") });
      return [...parts, textPart(ev.text || "")];
    }
    case "reasoning_delta": {
      const last = parts[parts.length - 1];
      if (last && last.type === "reasoning" && last.streaming)
        return replaceLast(parts, { ...last, md: (last.md || "") + (ev.text || "") });
      return [...parts, reasoningPart(ev.text || "")];
    }
    case "message_reset":
      // Escalation/retry boundary: a failed run leaves its partial answer stranded
      // in an OPEN streaming text part. Close every streaming TEXT part (immutably,
      // like the "final" case below but scoped to text) so the NEXT message_delta
      // opens a FRESH text part instead of concatenating the dead run's text onto
      // the retry's (message_delta appends only to a *streaming* trailing text part).
      return parts.map((p) => (p.type === "text" && p.streaming ? { ...p, streaming: false } : p));
    case "tool_call": {
      if (ev.tool === "TodoWrite") {
        const todos = (ev.args && ev.args.todos) || [];
        const idx = parts.findIndex((p) => p.type === "plan");
        const plan = { id: idx >= 0 ? parts[idx].id : nextId("pl"), type: "plan", todos };
        return idx >= 0 ? replaceAt(parts, idx, plan) : [...parts, plan];
      }
      // Replay idempotency: a redelivered tool_call (same harness id) must not make a second
      // card. Only when the event carries an id — id-less calls get minted ids and append as
      // before. (Deltas have no id at all; they rely on the seq gate in main.js instead.)
      if (ev.id != null && parts.some((p) => p.type === "tool" && p.id === ev.id)) return parts;
      return [...parts, toolPart(ev)];
    }
    case "tool_result": {
      const idx = parts.findIndex((p) => p.type === "tool" && p.id === ev.id);
      if (idx < 0) return parts;
      const prev = parts[idx];
      return replaceAt(parts, idx, {
        ...prev,
        status: ev.ok ? "ok" : "err",
        durationMs: prev.startedAt ? Date.now() - prev.startedAt : null,
        result: { ok: !!ev.ok, preview: ev.preview || "", diff: ev.diff || null, diffStats: ev.diff_stats || null, traces: ev.traces || null },
      });
    }
    case "file_write":
    case "file_read": {
      const isWrite = ev.type === "file_write";
      const base = (ev.path || "").split("/").pop() || ev.path || "";
      const part = toolPart({ id: ev.id || nextId("fw"), tool: ev.type, subtype: "file",
        verb: isWrite ? "Wrote" : "Read", target: base, risk: isWrite ? "mutate" : "read", args: { path: ev.path } });
      return [...parts, { ...part, status: "ok", result: { ok: true, diffSummary: ev.diff_summary || "" } }];
    }
    case "script_sanitized": {
      const part = toolPart({ tool: "sanitizer", subtype: "sanitizer",
        verb: ev.aborted ? "Sanitiser aborted" : "Cleaned", target: ev.target_name || "", risk: "mutate" });
      return [...parts, { ...part, status: ev.aborted ? "err" : "ok",
        result: { ok: !ev.aborted, diff: { original: ev.original || "", cleaned: ev.cleaned || "" }, summary: ev.summary || [] } }];
    }
    case "permission_request":
      // Replay idempotency: dedup by request_id so a redelivered request can't double the card.
      if (parts.some((p) => p.type === "permission" && p.id === ev.request_id)) return parts;
      return [...parts, { id: ev.request_id, type: "permission", tool: ev.tool, input: ev.input || {}, resolution: null }];
    case "user_question":
      if (parts.some((p) => p.type === "question" && p.id === ev.request_id)) return parts;
      return [...parts, { id: ev.request_id, type: "question", questions: ev.questions || [], resolution: null }];
    case "resolve_permission": {
      const idx = parts.findIndex((p) => p.type === "permission" && p.id === ev.request_id);
      return idx >= 0 ? replaceAt(parts, idx, { ...parts[idx], resolution: ev.decision }) : parts;
    }
    case "resolve_question": {
      const idx = parts.findIndex((p) => p.type === "question" && p.id === ev.request_id);
      return idx >= 0 ? replaceAt(parts, idx, { ...parts[idx], resolution: ev.answer }) : parts;
    }
    case "final":
      return parts.map((p) => (p.streaming
        ? { ...p, streaming: false, durationMs: p.type === "reasoning" && p.startedAt ? Date.now() - p.startedAt : p.durationMs }
        : p));
    default:
      return parts;
  }
}

// =============================================================================
// Section 2: Rendering layer (DOM)
// =============================================================================
// All imports below are ES module lazy-loads; no DOM calls happen at module
// evaluation time — only inside the exported render functions.

import { renderMarkdown } from "./agent-parts-md.js";
import { buildPermissionCard, buildQuestionCard } from "./agent-activity.js";
import { api } from "./api.js";
import { renderDiffInto } from "./diff-render.js";
import { syncTargetFromPath } from "./agent-store.js";

// --- Icon map: Lucide ic-* names for tool types / tool names -----------------
// Entries MUST reference names present in icons.css (ic-<name> class exists).
// Maps the tool/short-tool name to the icon suffix used in `ic ic-sm ic-<name>`.
const LUCIDE = {
  // Claude Code built-in tools
  Read:        "file-text",
  Write:       "edit-3",
  Edit:        "edit-3",
  MultiEdit:   "edit-3",
  Grep:        "search",
  Glob:        "search",
  Bash:        "terminal",
  TodoWrite:   "list",
  WebFetch:    "external-link",
  WebSearch:   "search",
  Task:        "sparkles",
  // CPQ scripting tools
  script_run:              "play",
  script_write:            "edit-3",
  script_read:             "file-text",
  script_list:             "list",
  script_validate_syntax:  "check-circle",
  // Sync tools
  sync_push:    "upload",
  sync_pull:    "download",
  sync_compare: "refresh-cw",
  // Template tools
  template_push: "upload",
  template_read: "file-text",
  // Configurator tools
  configure_open:          "sliders-horizontal",
  configure_set_attr:      "sliders-horizontal",
  configure_add_to_quote:  "plus",
  configure_fetch_traces:  "terminal-square",
  // Quote tools
  quote_lookup:     "search",
  quote_list_items: "list",
  // Rewind/history
  rewind:    "rewind",
  sanitizer: "wand-2",
};

const shortTool = (t) => (t && t.startsWith("mcp__") ? t.split("__").slice(-1)[0] : t);

/**
 * Return the Lucide icon suffix (ic-*) for a given tool name.
 * Falls back to "zap" (a generic "action" icon available in icons.css).
 */
export function iconClassFor(tool) {
  return LUCIDE[tool] || LUCIDE[shortTool(tool)] || "zap";
}

// --- Shared DOM helpers ------------------------------------------------------

function el(tag, cls, ...kids) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  for (const k of kids) {
    if (k == null || k === false) continue;
    e.appendChild(typeof k === "string" ? document.createTextNode(k) : k);
  }
  return e;
}

const fmtMs = (ms) => (ms == null ? "" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

// --- text renderer -----------------------------------------------------------

/**
 * Split markdown into top-level blocks (paragraph-level) for the streaming
 * renderer's block-freezing optimization.
 *
 * Blocks are separated by blank-line runs (matching the old /\n{2,}/ split)
 * EXCEPT inside an open ``` fence: a fenced region (its opening ``` line
 * through its closing ``` line) is kept in ONE block even when it contains
 * blank lines, and an unterminated fence (mid-stream) keeps its tail in one
 * block. Without this, a blank line inside a fenced code block (or a markdown
 * table) split the region across blocks, so each half rendered as broken markup
 * (unclosed <pre>, stray paragraph) — diverging from renderMarkdown() on the
 * whole text.
 *
 * PURE (no DOM) so it is unit-testable in plain node.
 * @param {string} md
 * @returns {string[]}
 */
export function splitStreamBlocks(md) {
  const lines = String(md || "").split("\n");
  const blocks = [];
  let cur = [];
  let inFence = false;      // inside an open ``` fence
  let pendingBreak = false; // saw a blank-line run outside a fence — flush before next content
  for (const line of lines) {
    // A blank line OUTSIDE a fence is a block boundary; collapse consecutive
    // blanks into a single break (matches /\n{2,}/). Blank lines INSIDE a fence
    // are code content and must not split the block.
    if (!inFence && line === "") { pendingBreak = true; continue; }
    if (pendingBreak) { blocks.push(cur.join("\n")); cur = []; pendingBreak = false; }
    cur.push(line);
    if (/^```/.test(line)) inFence = !inFence; // toggle on each fence-marker line
  }
  blocks.push(cur.join("\n"));
  return blocks;
}

/**
 * Render a text part as a sequence of .md-block children (one per top-level
 * markdown paragraph block).  Replaces the old single-node renderText.
 */
function renderText(part) {
  const d = el("div", "agent-part agent-part-text");
  d.dataset.partId = part.id;
  splitStreamBlocks(part.md).forEach((b) => {
    const e = el("div", "md-block");
    e.innerHTML = renderMarkdown(b);
    d.appendChild(e);
  });
  return d;
}

/**
 * In-place updater for a streaming text node.
 * Freezes all leading .md-block children (they are complete top-level blocks
 * that will never change) and only re-renders from the last existing block
 * index onward, appending new blocks as the text grows.
 *
 * This avoids re-parsing the entire markdown on every delta tick, preventing
 * flicker in already-rendered blocks.
 *
 * @param {HTMLElement} node  — the existing .agent-part-text element
 * @param {object}      part  — the updated text part (with new .md)
 */
export function updateTextNode(node, part) {
  const blocks = splitStreamBlocks(part.md);
  const existing = node.querySelectorAll(":scope > .md-block");
  // Re-render from the last existing block index onward (leaving prior blocks frozen).
  for (let i = existing.length - 1; i < blocks.length; i++) {
    let e = node.querySelectorAll(":scope > .md-block")[i];
    if (!e) { e = el("div", "md-block"); node.appendChild(e); }
    e.innerHTML = renderMarkdown(blocks[i] || "");
  }
}

// --- reasoning renderer (collapsed by default) -------------------------------

function renderReasoning(part) {
  const d = el("div", "agent-part agent-part-reasoning");
  d.dataset.partId = part.id;
  const label = part.streaming ? "Thinking…" : `Thought · ${fmtMs(part.durationMs)}`;
  const head = el("button", "reasoning-head",
    el("i", "ic ic-sm ic-activity"),
    el("span", "reasoning-label", label),
  );
  const body = el("div", "reasoning-body md-block");
  body.innerHTML = renderMarkdown(part.md || "");
  head.addEventListener("click", () => d.classList.toggle("open"));
  d.append(head, body);
  return d;
}

// --- tool renderer -----------------------------------------------------------

function renderTool(part) {
  const risk = part.risk || "read";
  const d = el("div", `agent-part agent-part-tool risk-${risk} st-${part.status || "running"}`);
  d.dataset.partId = part.id;
  const icon  = el("span", "tool-bullet");
  const verb   = el("span", "tool-verb",   part.verb   || shortTool(part.tool));
  const target = el("code", "tool-target", part.target || "");
  const dur    = el("span", "tool-dur",    fmtMs(part.durationMs));
  const statusIcon =
    part.status === "running" ? "ic-loader ic-spin" :
    part.status === "ok"      ? "ic-check" : "ic-x";
  const check  = el("span", "tool-check", el("i", `ic ic-sm ${statusIcon}`));
  const chevron = el("span", "tool-chevron", el("i", "ic ic-sm ic-chevron-right"));
  const head = el("button", "tool-head", icon, verb, target, dur, check, chevron);
  const body = el("div", "tool-body");
  renderToolBody(body, part);
  head.addEventListener("click", () => d.classList.toggle("open"));
  if (part.status === "err") {
    // Hard errors (mutations/deletes) stay loud + auto-expanded. Recoverable
    // read-tool errors (e.g. a missing-mirror grep) are non-blocking, so keep
    // them collapsed and mark them for de-emphasized styling instead of making
    // a completed run look broken.
    if (risk !== "read") d.classList.add("open");
    else d.classList.add("st-err-soft");
  }
  d.append(head, body);
  return d;
}

// Default body: args + preview. Specialized bodies (traces/diff/error) are
// layered in Tasks 7 & 8 by reassigning renderToolBody via setToolBodyRenderer.
// Exported as `let` so later tasks can monkey-patch it.
export let renderToolBody = (body, part) => {
  body.appendChild(el("div", "tool-body-label", "args"));
  const pre = el("pre", "tool-body-pre");
  pre.textContent = JSON.stringify(part.args || {}, null, 2);
  body.appendChild(pre);
  if (part.result && part.result.preview) {
    body.appendChild(el("div", "tool-body-label", "result"));
    const r = el("pre", "tool-body-pre");
    r.textContent = String(part.result.preview).slice(0, 2000);
    body.appendChild(r);
  }
};

/** Replace the default tool body renderer (used to add diff/traces). */
export function setToolBodyRenderer(fn) { renderToolBody = fn; }

// --- traces/error body renderer -------------------------------------
// Replaces the default args/preview body with:
//   - a .tool-body-error div for errored tools (auto-expanded by renderTool)
//   - a [traces N ▾] toggle + .tool-traces block (digest + per-error lines)
//   - falls back to args/preview when nothing rich is available
//
// SECURITY: ALL trace text is untrusted script output. This renderer uses
// textContent / createTextNode only -- never innerHTML on user/script data.

function renderToolBodyRich(body, part) {
  const r = part.result || {};

  // --- error body (auto-expanded by renderTool when status === "err") -------
  if (part.status === "err") {
    const errText =
      (r.traces && r.traces.errors && r.traces.errors[0] && r.traces.errors[0].message) ||
      r.preview ||
      "Tool failed";
    body.appendChild(el("div", "tool-body-error", String(errText)));
  }

  // --- traces toggle (shown for any tool_result that has a traces object) ---
  if (r.traces && (r.traces.action_count || (r.traces.errors && r.traces.errors.length) || r.traces.digest)) {
    const n = r.traces.action_count || (r.traces.errors ? r.traces.errors.length : 0);

    const toggle = el("button", "tool-traces-toggle");
    toggle.textContent = `traces ${n} ▾`;

    const tw = el("div", "tool-traces");

    // Digest line -- safe textContent via el()
    if (r.traces.digest) {
      tw.appendChild(el("div", "trace-line", String(r.traces.digest)));
    }

    // Error lines -- each may have a collapsible traceback
    for (const e of (r.traces.errors || [])) {
      const label = `⚠ ${e.script || ""}: ${e.message || ""}`;
      const line = el("div", "trace-line trace-err", label);
      if (e.traceback) {
        const st = el("pre", "trace-stack");
        st.textContent = String(e.traceback);  // textContent = safe for untrusted data
        st.hidden = true;
        line.addEventListener("click", () => { st.hidden = !st.hidden; });
        line.appendChild(st);
      }
      tw.appendChild(line);
    }

    tw.hidden = true;
    toggle.addEventListener("click", () => {
      tw.hidden = !tw.hidden;
      toggle.textContent = `traces ${n} ${tw.hidden ? "▾" : "▴"}`;
    });

    body.append(toggle, tw);
  }

  // --- sanitizer before/after diff (script_sanitized event) ----------------
  // r.diff is {original, cleaned} for sanitizer parts. Use textContent only —
  // original/cleaned are untrusted IronPython source, never innerHTML.
  if (r.diff && typeof r.diff === "object" && ("original" in r.diff || "cleaned" in r.diff)) {
    const wrap = el("div", "tool-diff");
    const mkPane = (label, text, cls) => {
      const box = el("div", "tool-diff-pane");
      box.appendChild(el("div", "tool-diff-label", label));
      const pre = el("pre", "tool-diff-pre " + cls);
      pre.textContent = text || "(empty)";
      box.appendChild(pre);
      return box;
    };
    wrap.appendChild(mkPane("before (raw)", r.diff.original || "", "tool-diff-before"));
    wrap.appendChild(mkPane("after (cleaned)", r.diff.cleaned || "", "tool-diff-after"));
    body.appendChild(wrap);
  }

  // --- lazy syncDiff toggle for file-write/read parts -----------------------
  // Only shown when the path maps to a known sync target (global-scripts,
  // custom-calculations, or ui-templates). Errors from syncDiff (no cloud
  // session, not found, network) show "No diff available." — never thrown/logged.
  if (part.subtype === "file" && part.args && part.args.path) {
    const tgt = syncTargetFromPath(part.args.path);
    if (tgt) {
      const toggle = el("button", "tool-diff-toggle", "diff ▾");
      const dc = el("div", "tool-diff");
      dc.hidden = true;
      let loaded = false;
      toggle.addEventListener("click", async () => {
        dc.hidden = !dc.hidden;
        toggle.textContent = `diff ${dc.hidden ? "▾" : "▴"}`;
        if (!dc.hidden && !loaded) {
          loaded = true;
          try {
            renderDiffInto(dc, await api.syncDiff(tgt.apiKind, tgt.name));
          } catch (_e) {
            dc.textContent = "No diff available.";
          }
        }
      });
      body.append(toggle, dc);
    }
  }

  // --- default args/preview fallback when nothing rich was rendered ---------
  if (!body.childNodes.length) {
    body.appendChild(el("div", "tool-body-label", "args"));
    const pre = el("pre", "tool-body-pre");
    pre.textContent = JSON.stringify(part.args || {}, null, 2);
    body.appendChild(pre);
    if (r.preview) {
      body.appendChild(el("div", "tool-body-label", "result"));
      const r2 = el("pre", "tool-body-pre");
      r2.textContent = String(r.preview).slice(0, 2000);
      body.appendChild(r2);
    }
  }
}

setToolBodyRenderer(renderToolBodyRich);

// --- plan renderer -----------------------------------------------------------

function renderPlan(part) {
  const d = el("div", "agent-part agent-part-plan");
  d.dataset.partId = part.id;
  d.appendChild(el("div", "plan-title", "Plan"));
  const ul = el("ul", "plan-list");
  for (const t of (part.todos || [])) {
    const gi = t.status === "completed" ? "ic-check-circle"
             : t.status === "in_progress" ? "ic-loader ic-spin" : "ic-circle";
    ul.appendChild(el("li", `plan-item st-${t.status || "pending"}`,
      el("span", "plan-glyph", el("i", `ic ic-sm ${gi}`)),
      el("span", "plan-text", t.content || ""),
    ));
  }
  d.appendChild(ul);
  return d;
}

// --- PART_RENDERERS registry -------------------------------------------------

export const PART_RENDERERS = {
  text:      renderText,
  reasoning: renderReasoning,
  tool:      renderTool,
  plan:      renderPlan,
};

export function registerPartRenderer(type, fn) { PART_RENDERERS[type] = fn; }

export function renderPart(part, convId) {
  const fn = PART_RENDERERS[part.type];
  return fn ? fn(part, convId) : el("div", "agent-part agent-part-unknown",
    // Safe: textContent is set in el() for string children, never innerHTML
    JSON.stringify(part));
}

// --- permission / question renderers (delegated to agent-activity.js) --------

registerPartRenderer("permission", (p, convId) => buildPermissionCard(p, convId));
registerPartRenderer("question",   (p, convId) => buildQuestionCard(p, convId));
